// Persisted stale-while-revalidate cache for dashboard data. Hooks seed
// their initial state from here so a returning user sees last-known data
// instantly, then every hook still revalidates against the API on mount.
//
// Keys are user-scoped (ztc:dash:v1:{email}:{resource}) so an account
// switch on a shared browser can never bleed data across users. The one
// exception is the auth record itself ("me") — the email that scopes
// every other key comes from it, so it is the unscoped bootstrap entry.
//
// Changing any cached payload shape requires bumping VERSION; old-version
// keys are removed by sweepStaleVersions() at app start.

const BASE_PREFIX = "ztc:dash:";
// v3 (IP-314): AdminToolUsage rows gained `unconnectedCount` and AdminToolCall
// rows gained `subToolUnconnectedCount`. Bumping evicts v2 caches whose rows
// lack the field (which would render undefined.toLocaleString() on first paint).
const VERSION = "v3";
const PREFIX = `${BASE_PREFIX}${VERSION}:`;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

type Envelope<T> = {
  v: 1;
  storedAt: number;
  value: T;
};

const defaultStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const cacheKey = (scope: string | null, resource: string): string =>
  scope === null ? `${PREFIX}${resource}` : `${PREFIX}${scope}:${resource}`;

export const readCache = <T>(key: string, storage: Storage | null = defaultStorage()): T | null => {
  if (storage === null) return null;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Envelope<T>>;
    if (parsed.v !== 1 || typeof parsed.storedAt !== "number" || !("value" in parsed)) {
      storage.removeItem(key);
      return null;
    }
    if (Date.now() - parsed.storedAt > MAX_AGE_MS) {
      storage.removeItem(key);
      return null;
    }
    return parsed.value as T;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // ignore — fail open to network
    }
    return null;
  }
};

const writeNow = <T>(key: string, value: T, storage: Storage | null): void => {
  if (storage === null) return;
  const envelope: Envelope<T> = { v: 1, storedAt: Date.now(), value };
  try {
    storage.setItem(key, JSON.stringify(envelope));
  } catch {
    // quota exceeded / private mode — degrade to uncached behavior
  }
};

// Writes are buffered until /api/me settles: an admin impersonating a
// customer must not persist that customer's data under the admin's own
// cache scope, and data hooks (SSE snapshots) can land before auth does.
type PersistMode = "pending" | "allowed" | "blocked";
let persistMode: PersistMode = "pending";
const bufferedWrites = new Map<string, unknown>();

export const resetPersistMode = (): void => {
  persistMode = "pending";
  bufferedWrites.clear();
};

export const setPersistAllowed = (allowed: boolean, storage: Storage | null = defaultStorage()): void => {
  persistMode = allowed ? "allowed" : "blocked";
  if (allowed) {
    for (const [key, value] of bufferedWrites) writeNow(key, value, storage);
  }
  bufferedWrites.clear();
};

export const writeCache = <T>(key: string, value: T, storage: Storage | null = defaultStorage()): void => {
  if (persistMode === "blocked") return;
  if (persistMode === "pending") {
    bufferedWrites.set(key, value);
    return;
  }
  writeNow(key, value, storage);
};

export const removeCache = (key: string, storage: Storage | null = defaultStorage()): void => {
  if (storage === null) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
};

const keysWithPrefix = (storage: Storage, prefix: string): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null && key.startsWith(prefix)) keys.push(key);
  }
  return keys;
};

export const clearDashboardCache = (storage: Storage | null = defaultStorage()): void => {
  bufferedWrites.clear();
  deferredUserWrites.clear();
  if (storage === null) return;
  try {
    for (const key of keysWithPrefix(storage, BASE_PREFIX)) storage.removeItem(key);
  } catch {
    // ignore
  }
};

export const sweepStaleVersions = (storage: Storage | null = defaultStorage()): void => {
  if (storage === null) return;
  try {
    for (const key of keysWithPrefix(storage, BASE_PREFIX)) {
      if (!key.startsWith(PREFIX)) storage.removeItem(key);
    }
  } catch {
    // ignore
  }
};

// Synchronous email lookup for hooks that need a user-scoped key before
// useAuth has revalidated (e.g. useClaws seeding on first render).
export const getCachedUserEmail = (storage: Storage | null = defaultStorage()): string | null => {
  const me = readCache<{ email?: unknown }>(cacheKey(null, "me"), storage);
  return me !== null && typeof me.email === "string" ? me.email : null;
};

// User-scoped read/write. On the very first session the email isn't known
// until /api/me lands, so writes made before then are parked by resource
// name and flushed by useAuth via flushDeferredUserWrites() — otherwise a
// new user's second visit would still be a fully cold load.
const deferredUserWrites = new Map<string, unknown>();

export const readUserCache = <T>(resource: string, storage: Storage | null = defaultStorage()): T | null => {
  const email = getCachedUserEmail(storage);
  return email === null ? null : readCache<T>(cacheKey(email, resource), storage);
};

export const writeUserCache = <T>(resource: string, value: T, storage: Storage | null = defaultStorage()): void => {
  const email = getCachedUserEmail(storage);
  if (email === null) {
    deferredUserWrites.set(resource, value);
    return;
  }
  writeCache(cacheKey(email, resource), value, storage);
};

export const flushDeferredUserWrites = (storage: Storage | null = defaultStorage()): void => {
  const email = getCachedUserEmail(storage);
  if (email === null) return;
  for (const [resource, value] of deferredUserWrites) {
    writeCache(cacheKey(email, resource), value, storage);
  }
  deferredUserWrites.clear();
};
